import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Lock, FileCheck, Users, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const complianceAreas = [
  { icon: Lock, title: 'Physical Safeguards', desc: 'Secure facilities, workstation security, and device controls' },
  { icon: Shield, title: 'Technical Safeguards', desc: 'Access controls, encryption, audit logs, and transmission security' },
  { icon: FileCheck, title: 'Administrative Safeguards', desc: 'Policies, procedures, workforce training, and risk assessments' },
  { icon: Users, title: 'BAA Compliance', desc: 'Business Associate Agreements with all healthcare partners' },
];

export const HIPAA = () => {
  return (
    <div data-testid="hipaa-page">
      <section className="relative py-24 bg-navy" data-testid="hipaa-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-emerald text-white font-semibold text-xs uppercase tracking-wider mb-6">
              Compliance
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white leading-tight mb-4">
              HIPAA Compliance
            </h1>
            <p className="text-lg text-navy-200 leading-relaxed">
              HealthCheck Medical Inc. is fully committed to protecting patient health information through comprehensive HIPAA compliance.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom max-w-5xl">
          <div className="mb-12">
            <h2 className="font-manrope font-bold text-3xl text-navy mb-4">Our Commitment to HIPAA</h2>
            <p className="text-navy-600 leading-relaxed">
              As a leading Management Services Organization, HealthCheck Medical Inc. handles Protected Health Information (PHI) with the utmost care and in strict compliance with the Health Insurance Portability and Accountability Act (HIPAA). Our comprehensive compliance program ensures that every aspect of our operations meets or exceeds federal healthcare privacy and security standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {complianceAreas.map((area) => (
              <Card key={area.title} className="border-silver-light shadow-card" data-testid={`compliance-${area.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-sm bg-emerald-50 flex items-center justify-center mb-4">
                    <area.icon className="w-6 h-6 text-emerald" />
                  </div>
                  <h3 className="font-manrope font-semibold text-navy text-lg mb-2">{area.title}</h3>
                  <p className="text-sm text-navy-600">{area.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="space-y-8 text-navy-700 leading-relaxed">
            <div>
              <h2 className="font-manrope font-bold text-2xl text-navy mb-4">Privacy Rule Compliance</h2>
              <p>
                We comply with the HIPAA Privacy Rule by establishing national standards for the protection of certain health information. Our policies ensure that PHI is used and disclosed only as necessary and permitted by law.
              </p>
            </div>

            <div>
              <h2 className="font-manrope font-bold text-2xl text-navy mb-4">Security Rule Compliance</h2>
              <p>
                Our Security Rule compliance program includes administrative, physical, and technical safeguards to protect electronic Protected Health Information (ePHI). Regular risk assessments, employee training, and security audits ensure ongoing compliance.
              </p>
            </div>

            <div>
              <h2 className="font-manrope font-bold text-2xl text-navy mb-4">Breach Notification</h2>
              <p>
                In the unlikely event of a breach, HealthCheck Medical Inc. maintains procedures to promptly investigate, mitigate, and notify affected parties in accordance with HIPAA Breach Notification Rules.
              </p>
            </div>

            <div>
              <h2 className="font-manrope font-bold text-2xl text-navy mb-4">Business Associate Agreements</h2>
              <p>
                We execute Business Associate Agreements (BAAs) with all healthcare clients and subcontractors that handle PHI, ensuring a comprehensive chain of compliance throughout our service delivery.
              </p>
            </div>

            <div>
              <h2 className="font-manrope font-bold text-2xl text-navy mb-4">Our Compliance Team</h2>
              <p className="mb-4">Our dedicated compliance team is available to address any HIPAA-related questions or concerns:</p>
              <div className="bg-navy-50 p-6 space-y-2">
                <p className="font-manrope font-semibold text-navy">HealthCheck Medical Inc. - Compliance Office</p>
                <p className="text-sm">Phone: (855) 831-1992</p>
                <p className="text-sm">Email: compliance@healthcentermedicalgroup.online</p>
                <p className="text-sm">Main Office: 7100 Commerce Way Ste 182, Brentwood, TN 37027</p>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-silver-light">
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-600 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
              data-testid="hipaa-contact-btn"
            >
              <Link to="/contact">Contact Compliance Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
